import { Global, ThemeProvider } from '@emotion/react'
import Box from '@mui/material/Box'
import { Auth } from '@aws-amplify/auth'
import * as Sentry from '@sentry/react'
import { SnackbarProvider } from 'notistack'
import { Outlet, useLocation } from 'react-router-dom'

import { Header } from './layout/header/HeaderContainer'
import { useAppSelector } from './redux/hooks'
import { RootState } from './redux/store'
import { globalStyle } from './styles/global'
import { theme } from './styles/theme'
import { useAuthInit } from './hooks/useAuthInit'

const App = () => {
  const location = useLocation()
  const headerMode = useAppSelector((state: RootState) => state.header.mode)
  const userInfo = useAppSelector((state: RootState) => state.authenticator.userInfo)
  Sentry.setUser({ email: userInfo?.email || '' })

  // Initialize auth
  useAuthInit()

  // Configure AWS Amplify Auth
  Auth.configure({
    Auth: {
      region: import.meta.env.VITE_AWS_REGION,
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolWebClientId: import.meta.env.VITE_COGNITO_USER_POOL_CLIENT_ID,
      authenticationFlowType: 'CUSTOM_AUTH'
    }
  })

  // Hide header on login page
  const isLoginPage = location.pathname === '/login'

  return (
    <div id="App">
      <Global styles={globalStyle} />
      <Sentry.ErrorBoundary
        showDialog
        fallback={<p>An error has occurred</p>}
        beforeCapture={(scope, error) => {
          scope.setTag('errorBoundary', `Message: ${error?.message}`)
        }}>
        <ThemeProvider theme={theme}>
          <SnackbarProvider maxSnack={3}>
            {!isLoginPage && <Header />}
            <Box
              maxWidth={headerMode === 'none' || isLoginPage ? '100vw' : 'xl'}
              sx={{
                m: headerMode === 'none' || isLoginPage ? '0 auto' : '3rem auto 0 auto',
                height: headerMode === 'none' || isLoginPage ? '100vh' : 'calc(100vh - 3rem)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}>
              <Outlet />
            </Box>
          </SnackbarProvider>
        </ThemeProvider>
      </Sentry.ErrorBoundary>
    </div>
  )
}

export default Sentry.withProfiler(App)
